import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMe } from "@workspace/api-client-react";
import { useAuth as useClerkAuth, useClerk } from "@clerk/clerk-react";
import { useCallback, useEffect, useState } from "react";

const VIEW_AS_USER_KEY = "duffells:viewAsUser";

// Access status drives the login screen: a signed-in user may still be
// refused by the API if they aren't a member of this app's Clerk org
// (no_access) or have been locally deactivated (deactivated).
export type AccessStatus =
  | "loading"
  | "signed_out"
  | "no_access"
  | "deactivated"
  | "ok";

function readViewAsUser(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(VIEW_AS_USER_KEY) === "1";
  } catch {
    return false;
  }
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { isLoaded: clerkLoaded, isSignedIn } = useClerkAuth();
  const { signOut } = useClerk();

  // Only ask the API who we are once Clerk says we're signed in — otherwise
  // the request is guaranteed to 401 and just adds noise.
  const {
    data: rawUser,
    isLoading: meLoading,
    error,
  } = useQuery({
    queryKey: ["/api/auth/me"],
    queryFn: () => getMe(),
    enabled: clerkLoaded && !!isSignedIn,
    retry: false,
    staleTime: Infinity,
  });

  const [viewAsUser, setViewAsUserState] = useState<boolean>(readViewAsUser);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === VIEW_AS_USER_KEY) setViewAsUserState(readViewAsUser());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isRealAdmin = rawUser?.role === "admin" || rawUser?.role === "super_admin";
  const isRealSuperAdmin = rawUser?.role === "super_admin";

  // If the real role isn't admin/super_admin, force viewAsUser off so it doesn't
  // linger for a non-admin who somehow has the flag set.
  const effectiveViewAsUser = isRealAdmin && viewAsUser;

  const setViewAsUser = useCallback((next: boolean) => {
    setViewAsUserState(next);
    try {
      if (next) window.localStorage.setItem(VIEW_AS_USER_KEY, "1");
      else window.localStorage.removeItem(VIEW_AS_USER_KEY);
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, []);

  // Overlay the role so every existing `user.role === "admin"` check in the
  // UI flips to "user" when the admin chooses to preview the user experience.
  // For super_admin we overlay to "admin" (not "user") so admin-only nav still
  // shows — super_admin is a superset of admin. Backend privileges unchanged.
  const overlayRole = effectiveViewAsUser
    ? "user"
    : rawUser?.role === "super_admin"
    ? "admin"
    : rawUser?.role;
  const user = rawUser
    ? {
        ...rawUser,
        role: overlayRole,
        realRole: rawUser.role,
        pagePermissions: (rawUser as any).pagePermissions ?? [],
        // In "view as user" preview, drop the per-group-admin overlay so
        // the admin sees exactly what a normal user with their granted
        // pagePermissions would see. Outside preview, expose the list so
        // hasGroupAdmin works correctly for real per-group admins.
        pageAdminPermissions: effectiveViewAsUser
          ? []
          : (rawUser as any).pageAdminPermissions ?? [],
      }
    : rawUser;

  // Derive a single access status from the Clerk + API state.
  const errorCode = (error as any)?.data?.code as string | undefined;
  let accessStatus: AccessStatus;
  if (!clerkLoaded || (isSignedIn && meLoading)) {
    accessStatus = "loading";
  } else if (!isSignedIn) {
    accessStatus = "signed_out";
  } else if (errorCode === "NO_APP_ACCESS") {
    accessStatus = "no_access";
  } else if (errorCode === "ACCOUNT_DEACTIVATED") {
    accessStatus = "deactivated";
  } else if (rawUser) {
    accessStatus = "ok";
  } else {
    // Signed in but the profile fetch failed for some other reason — treat
    // as not-yet-authorized (fail closed).
    accessStatus = error ? "no_access" : "loading";
  }

  const isLoading = accessStatus === "loading";

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      // Clear the view-as-user preview on sign-out so the next login starts clean.
      setViewAsUser(false);
      queryClient.clear();
      await signOut();
    } finally {
      setIsLoggingOut(false);
    }
  }, [queryClient, setViewAsUser, signOut]);

  return {
    user,
    isLoading,
    isAuthenticated: accessStatus === "ok",
    accessStatus,
    isRealAdmin,
    isRealSuperAdmin,
    isSuperAdmin: isRealSuperAdmin && !effectiveViewAsUser,
    viewAsUser: effectiveViewAsUser,
    setViewAsUser,
    logout,
    isLoggingOut,
  };
}
